import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DialogMode } from '../../../types/enum';
import {
    CodigoConfeccionItemResultsViewModel,
    CodigoConfeccionItemViewModel,
} from 'src/app/components/_mantenimiento/registros/codigosconfeccion/codigoconfeccion.types';
import { tap, catchError, of } from 'rxjs';
import { FilterRecintosSolicitudCamViewModel, AlmacenesSolicitudViewModel, TwAlmacenesSolicitudCamViewModel } from '../solicitud-recinto-cam.types';
import { SolicitudAlmacenCamService } from '../solicitud-almacen-cam.service';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { TwSolicitudCamViewModel, TwSolicitudCamViewModelBase } from '../../../types/solicitudcam-datosbasicos.types';
import { FilterSearchingBaseViewModel } from 'src/app/components/core/_infrastructure/types/common';
import { FormGroup } from '@angular/forms';

@Component({
    selector: 'app-solicitud-recinto-cam-wrapper-almacenes',
    templateUrl: './solicitud-recinto-cam-wrapper-almacenes.component.html',
    styleUrls: ['./solicitud-recinto-cam-wrapper-almacenes.component.scss'],
})
export class SolicitudRecintoCamWrapperAlmacenesComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() recintoListMode: DialogMode;
    @Input() readonly: boolean;
    @Input() solicitudCam: TwSolicitudCamViewModel;
    @Input() formGroup: FormGroup;
    @Input() onSave$: EventEmitter<void>;
    @Input() onAdd$: EventEmitter<void>;

    @Output() saved = new EventEmitter<number>();
    @Output() numberOfChanges = new EventEmitter<number>();

    almacenes: CodigoConfeccionItemResultsViewModel;
    recintoPIAListDialogVisible = false;
    payloadRecintosPIA: FilterRecintosSolicitudCamViewModel;
    dialogFilter: TwSolicitudCamViewModelBase;

    // Recinto PIA Detail view params
    recintoPIADetailVisible = false;
    recintoPIAIdSelected: number | null;

    private WarningMessageAlmacenesDuplicados = 'Algunos de los Almacenes seleccionados ya están asociados a la solicitud y no se han incluido de nuevo.';
    private successMessage = 'Actualización realizada satisfactoriamente';
    private defaultOrdenColumn = 'RegistroSanitario';

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private solicitudAlmacenCamService: SolicitudAlmacenCamService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.onSave$.subscribe(() => {
            this.onSaveAlmacenes();
        });
        this.onAdd$.subscribe(r => {
            this.onAddHandler();
        });
        this.form = this.formGroup;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.solicitudCam?.currentValue) {
            const solicitudCam = changes?.solicitudCam?.currentValue as TwSolicitudCamViewModel;
            if (solicitudCam) {
                this.dialogFilter = solicitudCam as TwSolicitudCamViewModelBase;
                this.initialLoadRecintosPIASolicitud(solicitudCam.Id!);
            }
        }
    }

    /**
     * Este método inicializa el payload que se utiliza para consultar los recintos de la solicitud
     * e invoca al servicio de obtención de recintos de la solicitud.
     * @param idSolicitudCam number Id. de la solicitud
     */
    private initialLoadRecintosPIASolicitud(idSolicitudCam: number): void {
        this.payloadRecintosPIA = new FilterRecintosSolicitudCamViewModel();
        this.payloadRecintosPIA.Asc = 0;
        this.payloadRecintosPIA.OrderBy = this.defaultOrdenColumn;
        this.payloadRecintosPIA.PageNumber = 0;
        this.payloadRecintosPIA.Id = idSolicitudCam;

        this.loadAlmacenes(this.payloadRecintosPIA);
    }

    private onAddHandler(): void {
        this.recintoPIAListDialogVisible = true;
    }

    private loadAlmacenes(payloadAlmacenes: FilterRecintosSolicitudCamViewModel): void {
        this.solicitudAlmacenCamService
            .getAlmacenesSolicitudCam(payloadAlmacenes)
            .pipe(
                tap((v: any) => {
                    this.almacenes = v;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * This method is executed after selecting Almacenes in the dialog.
     * @param selectedAlmacenes CodigoConfeccionItemViewModel[]
     * @returns void
     */
    onSelectedAlmacenes(selectedAlmacenes: CodigoConfeccionItemViewModel[]): void {
        selectedAlmacenes?.forEach(selectedAlmacen => {
            if (this.almacenIsDuplicated(selectedAlmacen)) {
                // No mostramos error. Simplemente no se incluye el Almacen en la lista.
                this.addWarningMessage(this.WarningMessageAlmacenesDuplicados);
                // console.log('Almacén duplicado. No se inserta', selectedAlmacen.Id);
                return;
            }
            // Se establece el Idusuario para indicar que es un registro introducido por el usuario
            selectedAlmacen.IdUsuario = this.loggedInUser?.Id;
            this.addAlmacenToList(selectedAlmacen);
        });
        this.recintoPIAListDialogVisible = false;
    }

    /**
     * Se guardarán los almacenes (nuevos) de la solicitud. Se eliminarán los marcados a 1 en el campo MarkToDelete.
     * Se valida que cumplan con las limitaciones de CA y disponibilidad, para poder ser
     * añadidos a la solicitud en el periodo.
     */
    private onSaveAlmacenes(): void {
        const almacenesSolicitudCam = this.convertAlmacenesSolicitud(this.almacenes?.ResultList);
        if (almacenesSolicitudCam.length === 0) {
            this.saved.emit(-1);
            return;
        }
        const almacenesSolicitudCamVM: AlmacenesSolicitudViewModel = new AlmacenesSolicitudViewModel();
        almacenesSolicitudCamVM.IdCA = this.solicitudCam.IdCa;
        almacenesSolicitudCamVM.IdPeriodoCam = this.solicitudCam.IdPeriodoCam;
        almacenesSolicitudCamVM.almacenesSolicitudCam = almacenesSolicitudCam;

        this.solicitudAlmacenCamService
            .updateAlmacenesSolicitudCam(almacenesSolicitudCamVM)
            .pipe(
                tap(() => {
                    this.addSuccessMessage(this.successMessage);
                    this.saved.emit(this.payloadRecintosPIA.Id);
                    this.getRecintosPIASolicitudCam(this.payloadRecintosPIA);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    if (error?.error?.length > 0) {
                        this.addErrorMessage(error.error[0]?.message);
                    }
                    this.formGroup.disable();
                    this.saved.emit(0);
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * Convierte el array usado en el paginador, en el array necesario para guardar los campos en los almacenes de la solicitud.
     * Los Almacenes insertados por el usuario y borrados, no se incluirán en el array resultante.
     * Solo se incluirán aquellos Almacenes originales que han sido "borrados"
     * y los nuevos Almacenes.
     * @param almacenes CodigoConfeccionItemViewModel[]
     * @returns TwAlmacenesSolicitudCamViewModel[]
     */
    private convertAlmacenesSolicitud(almacenes: CodigoConfeccionItemViewModel[]): TwAlmacenesSolicitudCamViewModel[] {
        const almacenesToBeSaved: TwAlmacenesSolicitudCamViewModel[] = [];
        almacenes?.forEach(almacen => {
            if (!almacen.IdUsuario && !almacen.MarkToDelete) {
                // Si el Almacén no es de los insertados mediante selección de Almacenes
                // y tampoco ha sido marcado para borrar
                // Se salta al siguiente Almacen.
                return; // Vamos al siguiente item del array.
            }
            const almacenSolicitudCamViewModel = new TwAlmacenesSolicitudCamViewModel();
            almacenSolicitudCamViewModel.IdAlmacen = almacen.Id;
            almacenSolicitudCamViewModel.MarkToDelete = almacen.MarkToDelete;
            almacenSolicitudCamViewModel.IdSolicitudCam = this.payloadRecintosPIA.Id;
            // Los siguientes datos se sobreescribirán en el core. Se mantienen por compatibilidad de tipos
            almacenSolicitudCamViewModel.IdUsuario = almacen.IdUsuario;
            almacenSolicitudCamViewModel.FechaCreacion = undefined;
            almacenesToBeSaved.push(almacenSolicitudCamViewModel);
        });

        return almacenesToBeSaved;
    }

    /**
     * Eliminar almacén de la solicitud
     * @param idAlmacen number|null
     * @return void
     */
    onDeleteAlmacen(idAlmacen: number | null): void {
        const almacen = this.almacenes?.ResultList?.find((value, index) => value?.Id == idAlmacen);
        if (!almacen) {
            return;
        }
        if (almacen.IdUsuario) {
            // Como es un almacén previamente añadido, lo borramos directamente de la lista.
            this.numberOfChanges.emit(-1);
            this.removeAlmacenFromList(almacen.Id);
        } else {
            almacen.MarkToDelete = 1;
            this.numberOfChanges.emit(1);
        }
        this.updateListAlmacenes(); // Así forzamos el ngOnChanges en el componente codigoconfeccion-list
    }

    /**
     * Se recrea la lista de Almacenes.
     * Así forzamos el ngOnChanges en el componente codigoconfeccion-list
     * @returns void
     */
    private updateListAlmacenes(): void {
        if (!this.almacenes) {
            return;
        }
        this.almacenes = { ...this.almacenes };
    }

    /**
     * Este método añade el Almacén pasado por parámetro a la lista de Almacenes que se utiliza en el paginador.
     * Actualiza también el contador de registros
     * e incrementa en 1 el contador de cambios.
     * Por último actualiza la lista de Almacenes.
     * @param almacen CodigoConfeccionItemViewModel
     */
    private addAlmacenToList(almacen: CodigoConfeccionItemViewModel): void {
        this.almacenes?.ResultList.push(almacen);
        this.almacenes.TotalCount = this.almacenes?.ResultList.length;
        this.numberOfChanges.emit(1);
        this.updateListAlmacenes();
    }

    /**
     * Este método borra el Almacén indicado por parámetro, de la lista de Almacenes (obtenidos en el paginador).
     * Pero solo lo borra "físicamente" del array, si es un registro de los añadidos por el usuario.
     * @param idAlmacen number
     * @returns void
     */
    private removeAlmacenFromList(idAlmacen: number): void {
        const index = this.almacenes?.ResultList.findIndex(x => x.Id == idAlmacen && x.IdUsuario !== null);
        if (index === undefined) {
            return;
        }
        this.almacenes?.ResultList.splice(index, 1);
        this.almacenes.TotalCount = this.almacenes?.ResultList.length;
    }

    /**
     * Deshacer Eliminar el Almacén de la solicitud.
     * Solo se pueden "deshacer" los recintos que han venido originalmente de la base de datos.
     * @param idAlmacen number|null
     * @return void
     */
    onUndoDeleteAlmacen(idAlmacen: number | null): void {
        const almacenes = this.almacenes?.ResultList?.find((value, index) => value?.Id == idAlmacen);
        if (!almacenes) {
            return;
        }
        almacenes.MarkToDelete = 0;
        this.numberOfChanges.emit(-1);
        this.updateListAlmacenes(); // Así forzamos el ngOnChanges en el componente codigoconfeccion-list
    }

    /**
     * Este método busca el Almacén indicado por parámetro, en el array de Almacenes obtenido en el paginador.
     * Devuelve true si lo encuentra o false en caso contrario.
     * @param almacen CodigoConfeccionItemViewModel
     * @returns boolean
     */
    private almacenIsDuplicated(almacen: CodigoConfeccionItemViewModel): boolean {
        const itemFound = this.almacenes?.ResultList.find(item => item.Id === almacen.Id);
        return itemFound !== undefined;
    }

    /**
     * It will be invoked when using the paginator
     * @param lazyLoadParams FilterSearchingBaseViewModel
     * @return void
     */
    onSearchLazy(lazyLoadParams: FilterSearchingBaseViewModel): void {
        const payload = {
            ...this.payloadRecintosPIA,
            PageNumber: lazyLoadParams.PageNumber,
            Asc: lazyLoadParams.Asc,
            OrderBy: lazyLoadParams.OrderBy,
        } as FilterRecintosSolicitudCamViewModel;
        this.getRecintosPIASolicitudCam(payload);
    }

    /**
     * Este método obtiene la lista de almacenes de la solicitud indicada por parámetro.
     * @param payload FilterRecintosSolicitudCamViewModel
     * @returns void
     */
    private getRecintosPIASolicitudCam(payload: FilterRecintosSolicitudCamViewModel): void {
        this.payloadRecintosPIA = { ...payload } as FilterRecintosSolicitudCamViewModel;
        if (!payload) {
            return;
        }
        this.loadAlmacenes(this.payloadRecintosPIA);
    }

    onViewHandler(idRecintoPIA: number | null): void {
        this.recintoPIAIdSelected = idRecintoPIA;
        this.recintoPIADetailVisible = true;
    }

    /**
     * Se captura el evento que indica que la ventana de diálogo que muestra el detalle del almacén
     * se ha cerrado.
     * @param $event any
     */
    onAlmacenDialogClosed($event: any): void {
        this.recintoPIADetailVisible = false;
    }
}
